import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { readScreenShortSide, shouldLockPortrait } from '../utils/portraitOrientation';
import './PortraitOnlyGate.css';

function readShouldLock() {
  if (typeof window === 'undefined') return false;
  return shouldLockPortrait({
    userAgent: navigator.userAgent,
    maxTouchPoints: navigator.maxTouchPoints,
    screenShortSide: readScreenShortSide(window.screen),
  });
}

function isLandscape() {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia('(orientation: landscape)').matches) return true;
  return window.innerWidth > window.innerHeight;
}

function tryLockPortrait() {
  try {
    const orientation = window.screen?.orientation;
    if (orientation && typeof orientation.lock === 'function') {
      orientation.lock('portrait').catch(() => {});
    } else if (typeof window.screen?.lockOrientation === 'function') {
      window.screen.lockOrientation('portrait');
    } else if (typeof window.screen?.mozLockOrientation === 'function') {
      window.screen.mozLockOrientation('portrait');
    } else if (typeof window.screen?.msLockOrientation === 'function') {
      window.screen.msLockOrientation('portrait');
    }
  } catch {
    // Browser may deny lock outside fullscreen / installed PWA.
  }
}

function tryUnlockOrientation() {
  try {
    const orientation = window.screen?.orientation;
    if (orientation && typeof orientation.unlock === 'function') {
      orientation.unlock();
    }
  } catch {
    // Unlock is a no-op when the page never locked orientation.
  }
}

/** Blocks landscape on iPhone and Android phones. Big displays stay rotatable. */
export default function PortraitOnlyGate() {
  const { t } = useTranslation();
  const [blocked, setBlocked] = useState(() => readShouldLock() && isLandscape());

  useEffect(() => {
    const update = () => {
      const lockPortrait = readShouldLock();
      setBlocked(lockPortrait && isLandscape());
      if (lockPortrait) tryLockPortrait();
      else tryUnlockOrientation();
    };

    update();
    window.addEventListener('orientationchange', update);
    window.addEventListener('resize', update);
    const mq = window.matchMedia('(orientation: portrait)');
    mq.addEventListener('change', update);

    return () => {
      window.removeEventListener('orientationchange', update);
      window.removeEventListener('resize', update);
      mq.removeEventListener('change', update);
    };
  }, []);

  if (!blocked) {
    return null;
  }

  return (
    <div className="portrait-only-gate" role="alertdialog" aria-modal="true" aria-live="assertive">
      <div className="portrait-only-gate__card">
        <div className="portrait-only-gate__icon" aria-hidden="true">
          <span className="portrait-only-gate__phone" />
        </div>
        <p className="portrait-only-gate__title">{t('portraitOnlyMessage')}</p>
        <p className="portrait-only-gate__hint">{t('portraitOnlyHint')}</p>
      </div>
    </div>
  );
}
